<template>
  <div class="product-card-grid">
    <div v-if="!items || !items.length" class="product-card-grid__empty py-5 text-center">
      {{ $t("catalogEmptyProducts") }}
    </div>
    <template v-else>
      <transition name="catalog-expand">
        <section
          v-if="expandedRow"
          :key="expandedRow.productId"
          class="product-card-grid__expanded"
          :class="expandRevealClass"
        >
        <article
          class="product-card product-card--sheet"
          :class="{ 'product-card--selected': selectedProductId === expandedRow.productId }"
        >
          <div class="product-card__accent" aria-hidden="true" />

          <header class="product-card-expanded__top">
            <div class="product-card-expanded__top-text">
              <h3 class="product-card__title">{{ expandedRow.prodName || "—" }}</h3>
              <p
                v-if="partyLine(expandedRow)"
                class="product-card__subtitle"
                :class="partySubtitleClass(expandedRow)"
              >
                {{ partyLine(expandedRow) }}
              </p>
            </div>
            <div class="product-card-expanded__top-meta">
              <span v-if="expandedRow.productId != null" class="product-card__id">#{{ expandedRow.productId }}</span>
            </div>
          </header>

          <div class="product-card-expanded__body">
            <ul class="product-card__metrics product-card__metrics--sheet" aria-label="Key figures">
              <li class="product-card__metric">
                <span class="product-card__metric-label">{{ $t("premiumLabel") }}</span>
                <span class="product-card__metric-value product-card__metric-value--premium">{{
                  formatPremium(expandedRow)
                }}</span>
              </li>
              <li class="product-card__metric">
                <span class="product-card__metric-label">{{ $t("sumInsuredLabel") }}</span>
                <span class="product-card__metric-value product-card__metric-value--extra">{{
                  formatSumInsured(expandedRow)
                }}</span>
              </li>
              <li class="product-card__metric">
                <span class="product-card__metric-label">{{ $t("labelPeriod") }}</span>
                <span class="product-card__metric-value product-card__metric-value--period">{{
                  formatPeriod(expandedRow)
                }}</span>
              </li>
            </ul>

            <div class="product-card-expanded__columns">
              <div class="product-card-expanded__col product-card-expanded__col--services">
                <div class="product-card__services-block">
                  <span class="product-card__row-label">{{ $t("catalogIncludedServices") }}</span>
                  <ul v-if="serviceList(expandedRow).length" class="product-card__services product-card__services--sheet" aria-label="Included services">
                    <li v-for="(name, idx) in serviceList(expandedRow)" :key="`ex-${expandedRow.productId}-svc-${idx}`">
                      {{ name }}
                    </li>
                  </ul>
                  <p v-else class="product-card__list-empty">—</p>
                </div>
              </div>
              <div class="product-card-expanded__col product-card-expanded__col--exceptions">
                <div class="product-card__services-block">
                  <span class="product-card__row-label product-card__row-label--exceptions">{{ $t("ineligibility") }}</span>
                  <ul v-if="exceptList(expandedRow).length" class="product-card__services product-card__services--exceptions product-card__services--sheet" aria-label="Exclusions">
                    <li v-for="(name, idx) in exceptList(expandedRow)" :key="`ex-${expandedRow.productId}-exc-${idx}`">
                      {{ name }}
                    </li>
                  </ul>
                  <p v-else class="product-card__list-empty">—</p>
                </div>
              </div>
            </div>
          </div>

          <footer class="product-card__footer product-card__footer--split">
            <button type="button" class="product-card__btn product-card__btn--ghost" @click="collapseExpand">
              {{ $t("catalogCollapseDetails") }}
            </button>
            <button
              type="button"
              class="product-card__btn product-card__btn--accent"
              @click="$emit('select-product', expandedRow)"
            >
              {{ $t("catalogSelectProductBtn") }}
            </button>
          </footer>
        </article>
        </section>
      </transition>

      <transition-group name="catalog-card" tag="div" class="product-card-grid__inner">
        <article
          v-for="row in compactItems"
          :key="row.productId"
          class="product-card"
          :class="{ 'product-card--selected': selectedProductId === row.productId }"
        >
          <div class="product-card__accent" aria-hidden="true" />

          <header class="product-card__head">
            <div class="product-card__head-text">
              <h3 class="product-card__title">{{ row.prodName || "—" }}</h3>
              <p v-if="partyLine(row)" class="product-card__subtitle" :class="partySubtitleClass(row)">
                {{ partyLine(row) }}
              </p>
            </div>
            <span v-if="row.productId != null" class="product-card__id">#{{ row.productId }}</span>
          </header>

          <ul class="product-card__metrics" aria-label="Key figures">
            <li class="product-card__metric">
              <span class="product-card__metric-label">{{ $t("premiumLabel") }}</span>
              <span class="product-card__metric-value product-card__metric-value--premium">{{
                formatPremium(row)
              }}</span>
            </li>
            <li class="product-card__metric">
              <span class="product-card__metric-label">{{ $t("sumInsuredLabel") }}</span>
              <span class="product-card__metric-value product-card__metric-value--extra">{{
                formatSumInsured(row)
              }}</span>
            </li>
            <li class="product-card__metric">
              <span class="product-card__metric-label">{{ $t("labelPeriod") }}</span>
              <span class="product-card__metric-value product-card__metric-value--period">{{
                formatPeriod(row)
              }}</span>
            </li>
          </ul>

          <div class="product-card__details">
            <div class="product-card__services-block">
              <span class="product-card__row-label">{{ $t("catalogIncludedServices") }}</span>
              <ul v-if="serviceList(row).length" class="product-card__services" aria-label="Included services">
                <li v-for="(name, idx) in serviceList(row)" :key="`${row.productId}-svc-${idx}`">
                  {{ name }}
                </li>
              </ul>
              <p v-else class="product-card__list-empty">—</p>
            </div>
            <div class="product-card__services-block">
              <span class="product-card__row-label product-card__row-label--exceptions">{{ $t("ineligibility") }}</span>
              <ul v-if="exceptList(row).length" class="product-card__services product-card__services--exceptions" aria-label="Exclusions">
                <li v-for="(name, idx) in exceptList(row)" :key="`${row.productId}-exc-${idx}`">
                  {{ name }}
                </li>
              </ul>
              <p v-else class="product-card__list-empty">—</p>
            </div>
          </div>


          <footer class="product-card__footer product-card__footer--split">
            <button type="button" class="product-card__btn product-card__btn--ghost" @click="expandRow(row)">
              {{ $t("catalogViewDetails") }}
            </button>
            <button type="button" class="product-card__btn product-card__btn--accent" @click="$emit('select-product', row)">
              {{ $t("catalogSelectProductBtn") }}
            </button>
          </footer>
        </article>
      </transition-group>
    </template>
  </div>
</template>

<script>
import { isProductLegalEntity, productPartyKind } from "../catalogProductParty.js";

export default {
  name: "NewsrcProductCardGrid",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    selectedProductId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["select-product"],
  data() {
    return {
      expandedProductId: null,
      /** Horizontal reveal direction for expanded sheet (from card column). */
      expandRevealClass: "catalog-reveal--left",
    };
  },
  computed: {
    expandedRow() {
      if (this.expandedProductId == null) return null;
      return this.items.find((r) => r.productId === this.expandedProductId) || null;
    },
    compactItems() {
      if (this.expandedProductId == null) return this.items;
      return this.items.filter((r) => r.productId !== this.expandedProductId);
    },
  },
  watch: {
    items: {
      handler() {
        if (
          this.expandedProductId != null &&
          !this.items.some((r) => r.productId === this.expandedProductId)
        ) {
          this.expandedProductId = null;
        }
      },
      deep: true,
    },
  },
  methods: {
    expandRow(row) {
      if (!row || row.productId == null) return;
      this.expandRevealClass = this.computeExpandRevealClass(row);
      this.expandedProductId = row.productId;
    },
    computeExpandRevealClass(row) {
      const idx = this.items.findIndex((r) => r.productId === row.productId);
      if (idx < 0) return "catalog-reveal--left";
      const w = typeof window !== "undefined" ? window.innerWidth : 1200;
      let cols = 1;
      if (w >= 1100) cols = 3;
      else if (w >= 992) cols = 3;
      else if (w >= 640) cols = 2;
      const col = idx % cols;
      if (cols === 1) return "catalog-reveal--left";
      if (cols === 2) return col === 0 ? "catalog-reveal--left" : "catalog-reveal--right";
      if (col === 0) return "catalog-reveal--left";
      if (col === 1) return "catalog-reveal--center";
      return "catalog-reveal--right";
    },
    collapseExpand() {
      this.expandedProductId = null;
    },
    /** Grouped integer amount (no suffix); pair with currencyAZN where needed */
    formatMoneyInteger(value) {
      const n = Math.round(Number(value));
      if (Number.isNaN(n)) return "—";
      return n.toLocaleString();
    },
    formatMoneyAzn(value) {
      const s = this.formatMoneyInteger(value);
      if (s === "—") return "—";
      return `${s} ${this.$t("currencyAZN")}`;
    },
    formatPremium(row) {
      let num = null;
      if (row.premiumAmount != null && !Number.isNaN(Number(row.premiumAmount))) {
        num = Math.round(Number(row.premiumAmount));
      } else if (row.premium != null && !Number.isNaN(Number(row.premium))) {
        num = Math.round(Number(row.premium));
      } else {
        const raw = row.premiumDisplay;
        if (raw != null && String(raw).trim() !== "") {
          const n = parseFloat(String(raw).replace(/\s/g, "").replace(",", "."));
          if (!Number.isNaN(n)) num = Math.round(n);
        }
      }
      if (num === null) return "—";
      return this.formatMoneyAzn(num);
    },
    sumInsuredNumber(row) {
      if (row.sumInsured != null && !Number.isNaN(Number(row.sumInsured))) {
        return Number(row.sumInsured);
      }
      if (row.sumInsuredAmount != null && !Number.isNaN(Number(row.sumInsuredAmount))) {
        return Number(row.sumInsuredAmount);
      }
      const s = row.sumInsuredDisplay;
      if (s == null || !String(s).trim()) return null;
      const digits = String(s).replace(/\D/g, "");
      if (!digits) return null;
      const parsed = parseInt(digits, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    formatSumInsured(row) {
      const n = this.sumInsuredNumber(row);
      if (n === null) return "—";
      return this.formatMoneyAzn(n);
    },
    formatPeriod(row) {
      const raw = row?.period ?? row?.periodMonths;
      if (raw == null || raw === "") return "—";
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return "—";
      return String(Math.round(n));
    },
    normalizeNameEntry(s) {
      if (s == null) return "";
      let t = String(s).trim();
      if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
        t = t.slice(1, -1).trim();
      }
      return t;
    },
    resolveNameItem(item) {
      if (item == null) return "";
      if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
        return this.normalizeNameEntry(item);
      }
      if (typeof item === "object") {
        const fields = [
          item.serviceName,
          item.ServiceName,
          item.exceptName,
          item.ExceptName,
          item.name,
          item.Name,
          item.title,
          item.Title,
          item.text,
          item.label,
          item.value,
          item.description,
        ];
        for (const f of fields) {
          const t = this.normalizeNameEntry(f);
          if (t) return t;
        }
        const strings = Object.values(item).filter(
          (v) => typeof v === "string" && String(v).trim()
        );
        if (strings.length === 1) return this.normalizeNameEntry(strings[0]);
      }
      return "";
    },
    pushNameSegments(out, raw) {
      const seg = this.resolveNameItem(raw);
      if (!seg) return;
      if (/[,;|]/.test(seg)) {
        out.push(...seg.split(/[,;|]/u).map((s) => s.trim()).filter(Boolean));
      } else {
        out.push(seg);
      }
    },
    coerceNameSource(v) {
      if (v == null) return null;
      if (Array.isArray(v)) return v;
      if (typeof v === "string") {
        const t = v.trim();
        if (!t) return null;
        if (t.startsWith("[") || t.startsWith("{")) {
          try {
            const parsed = JSON.parse(t);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && typeof parsed === "object") return Object.values(parsed);
          } catch {
            /* plain string */
          }
        }
        return [t];
      }
      if (typeof v === "object") {
        if (Array.isArray(v.items)) return v.items;
        if (Array.isArray(v.data)) return v.data;
        return Object.values(v);
      }
      return null;
    },
    nameList(row, ...keys) {
      const out = [];
      for (const key of keys) {
        const source = this.coerceNameSource(row[key]);
        if (!source) continue;
        for (const item of source) {
          this.pushNameSegments(out, item);
        }
      }
      return out;
    },
    serviceList(row) {
      const fromApi = this.nameList(row, "serviceNames", "ServiceNames");
      if (fromApi.length) return fromApi;
      return this.nameList(row, "includedServices");
    },
    exceptList(row) {
      const fromApi = this.nameList(row, "exceptNames", "ExceptNames");
      if (fromApi.length) return fromApi;
      const inel = row.ineligibility;
      if (typeof inel === "string" && inel.trim()) {
        const t = inel.trim();
        if (/^\d+$/.test(t)) return [];
        const parts = t.split(/[.;]/u).map((s) => s.trim()).filter(Boolean);
        if (parts.length > 1) return parts;
        return [t];
      }
      return [];
    },
    partyLine(row) {
      const kind = productPartyKind(row);
      if (kind === "legal") return this.$t("partyLegalEntity");
      if (kind === "individual") return this.$t("partyNaturalPerson");
      return "";
    },
    partySubtitleClass(row) {
      return isProductLegalEntity(row)
        ? "product-card__subtitle--legal"
        : "product-card__subtitle--individual";
    },
  },
};
</script>

<style scoped>
/* Motion tokens — soft deceleration, long tail = calmer */
.product-card-grid {
  --catalog-ease-out: cubic-bezier(0.22, 1, 0.45, 1);
  --catalog-ease-soft: cubic-bezier(0.33, 1, 0.68, 1);
  --catalog-dur-reveal: 0.72s;
  --catalog-dur-fade: 0.48s;
  --catalog-dur-grid: 0.58s;
  width: 100%;
  position: relative;
}

/* ---- Expand panel: horizontal reveal (smooth clip + gentle opacity) ---- */
.catalog-expand-enter-active {
  transition:
    clip-path var(--catalog-dur-reveal) var(--catalog-ease-out),
    opacity var(--catalog-dur-fade) var(--catalog-ease-soft) 0.04s;
}

.catalog-expand-leave-active {
  transition: opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.catalog-expand-leave-to {
  opacity: 0;
}

/* Open: grow from left edge to the right */
.catalog-expand-enter-from.catalog-reveal--left {
  clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
  opacity: 0.93;
}

/* Open: grow from center outward */
.catalog-expand-enter-from.catalog-reveal--center {
  clip-path: polygon(50% 0, 50% 0, 50% 100%, 50% 100%);
  opacity: 0.93;
}

/* Open: grow from right edge to the left */
.catalog-expand-enter-from.catalog-reveal--right {
  clip-path: polygon(100% 0, 100% 0, 100% 100%, 100% 100%);
  opacity: 0.93;
}

.catalog-expand-enter-to.catalog-reveal--left,
.catalog-expand-enter-to.catalog-reveal--center,
.catalog-expand-enter-to.catalog-reveal--right,
.catalog-expand-leave-from {
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  opacity: 1;
}

/* ---- Grid: silky reflow ---- */
.catalog-card-move {
  transition: transform var(--catalog-dur-grid) var(--catalog-ease-out);
}

.product-card-grid__expanded {
  width: 100%;
  margin-bottom: 1.75rem;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  contain: paint;
  will-change: clip-path;
}

/* Metrics: short staggered rise after clip starts (reads as one motion) */
.product-card--sheet .product-card__metrics--sheet {
  animation: catalogMetricsRise 0.56s var(--catalog-ease-out) both;
  animation-delay: 0.12s;
}

@keyframes catalogMetricsRise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Detail column: soft settle + slight scale (matches reveal side) */
.product-card-expanded__col--text {
  transform-origin: left center;
  animation: catalogDetailFill 0.62s var(--catalog-ease-out) both;
  animation-delay: 0.14s;
}

.catalog-reveal--right .product-card-expanded__col--text {
  transform-origin: right center;
}

.catalog-reveal--center .product-card-expanded__col--text {
  transform-origin: center top;
}

@keyframes catalogDetailFill {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.988);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .catalog-expand-enter-active,
  .catalog-expand-leave-active,
  .catalog-card-move {
    transition-duration: 0.01ms !important;
  }

  .product-card-expanded__col--text,
  .product-card--sheet .product-card__metrics--sheet {
    animation: none !important;
  }
}

.product-card-grid__inner {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: stretch;
  position: relative;
}

@media (min-width: 640px) {
  .product-card-grid__inner {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1100px) {
  .product-card-grid__inner {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.75rem;
  }
}

.product-card-grid__empty {
  color: var(--agent-product-empty-text);
}

.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  padding: 0;
  border-radius: 16px;
  overflow: hidden;
  background: var(--agent-product-card-bg);
  border: 1px solid var(--agent-product-card-border);
  box-shadow: var(--agent-product-card-shadow);
  backdrop-filter: var(--agent-product-card-backdrop);
  transition:
    transform 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease;
}

.product-card--sheet {
  border-radius: 18px;
  border-color: var(--agent-product-card-sheet-border);
  box-shadow: var(--agent-product-card-sheet-shadow);
}

.product-card--selected {
  border-color: var(--agent-product-card-selected-border);
  box-shadow: var(--agent-product-card-selected-shadow);
}

.product-card:hover:not(.product-card--sheet) {
  transform: translateY(-4px);
  border-color: var(--agent-product-card-hover-border);
  box-shadow: var(--agent-product-card-hover-shadow);
}

.product-card__accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--agent-product-card-accent);
  opacity: 0.95;
  border-radius: 16px 0 0 16px;
}

.product-card-expanded__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem 1.25rem 1rem 1.4rem;
  border-bottom: 1px solid var(--agent-product-card-divider);
}

.product-card-expanded__top-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.product-card-expanded__body {
  padding: 0 0 0.5rem;
}

.product-card-expanded__columns {
  display: grid;
  gap: 1.35rem;
  padding: 1rem 1.25rem 0.25rem 1.4rem;
  grid-template-columns: 1fr;
}

@media (min-width: 880px) {
  .product-card-expanded__columns {
    gap: 1.5rem 2rem;
    /* Services column gets more width for long names / numbers */
    grid-template-columns: minmax(300px, 0.58fr) minmax(240px, 0.42fr);
  }
}

@media (min-width: 1200px) {
  .product-card-expanded__columns {
    grid-template-columns: minmax(380px, 0.62fr) minmax(280px, 0.38fr);
  }
}

.product-card-expanded__col--services {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.product-card-expanded__col--exceptions {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.product-card-expanded__col--text {
  min-width: 0;
}

.product-card__row-label--exceptions {
  color: var(--agent-product-row-label-exc);
}

.product-card__services--exceptions li {
  color: var(--agent-product-exc-chip-text);
  background: var(--agent-product-exc-chip-bg);
  border-color: var(--agent-product-exc-chip-border);
}

.product-card__metrics--sheet {
  margin: 0 1.25rem 0 1.4rem;
  padding: 1rem 0 1rem;
  border-bottom: 1px solid var(--agent-product-card-divider-soft);
}

@media (min-width: 520px) {
  .product-card__metrics--sheet {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.product-card__row--sheet .product-card__row-value--full {
  display: block;
  -webkit-line-clamp: unset;
  -webkit-box-orient: unset;
  overflow: visible;
  white-space: normal;
}

.product-card__row--sheet.product-card__row--block .product-card__row-value {
  -webkit-line-clamp: unset;
  overflow: visible;
}

.product-card__more-info {
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.55;
  color: var(--agent-product-card-text);
}

.product-card__ghost-btn {
  border: 1px solid var(--agent-product-btn-ghost-border);
  background: var(--agent-product-btn-ghost-bg);
  color: var(--agent-product-btn-ghost-text);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.4rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.product-card__ghost-btn:hover {
  border-color: var(--agent-product-card-hover-border);
  background: var(--agent-product-btn-ghost-hover-bg);
}

.product-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1.15rem 1.2rem 0.85rem 1.35rem;
  border-bottom: 1px solid var(--agent-product-card-divider-soft);
}

.product-card__head-text {
  min-width: 0;
}

.product-card__title {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
  color: var(--agent-product-card-title);
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.product-card--sheet .product-card__title {
  font-size: 1.25rem;
}

.product-card__subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.product-card__subtitle--individual {
  color: var(--agent-product-subtitle-individual);
}

.product-card__subtitle--legal {
  color: var(--agent-product-subtitle-legal);
}

.product-card__id {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: var(--agent-product-id-text);
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  background: var(--agent-product-id-bg);
  border: 1px solid var(--agent-product-id-border);
}

.product-card__metrics {
  margin: 0;
  padding: 0.85rem 1.2rem 0.75rem 1.35rem;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  border-bottom: 1px solid var(--agent-product-card-divider-soft);
}

@media (min-width: 520px) {
  .product-card__metrics {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.product-card__metric {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.45rem 0.5rem;
  border-radius: 10px;
  background: var(--agent-product-card-metric-bg);
  border: 1px solid var(--agent-product-card-metric-border);
}

.product-card__metric-label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--agent-product-card-muted);
}

.product-card__metric-value {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--agent-product-card-text);
  font-variant-numeric: tabular-nums;
}

.product-card__metric-value--premium {
  font-size: 0.84rem;
  font-weight: 600;
  line-height: 1.25;
}

.product-card__metric-value--extra {
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-variant-numeric: normal;
}

.product-card__metrics--sheet .product-card__metric-value--extra {
  -webkit-line-clamp: 3;
}

.product-card__details {
  flex: 1;
  padding: 0.85rem 1.2rem 0.5rem 1.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.product-card__list-empty {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: var(--agent-product-card-muted);
}

.product-card__services-block {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.product-card__services-block .product-card__row-label {
  display: block;
}

.product-card__services {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.product-card__services li {
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  padding: 0.28rem 0.5rem;
  border-radius: 8px;
  color: var(--agent-product-chip-text);
  background: var(--agent-product-chip-bg);
  border: 1px solid var(--agent-product-chip-border);
  letter-spacing: 0.02em;
}

/* Expanded sheet only: wider service area + multi-column chip grid */
.product-card__services.product-card__services--sheet {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
  gap: 0.45rem 0.5rem;
}

.product-card__services.product-card__services--sheet li {
  font-size: 0.8125rem;
  line-height: 1.35;
  padding: 0.4rem 0.65rem;
  min-height: 2.1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  word-break: break-word;
  hyphens: auto;
}

.product-card__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.65rem;
  font-size: 0.8rem;
  line-height: 1.45;
}

.product-card__row--block {
  flex-direction: column;
  align-items: stretch;
  gap: 0.2rem;
}

.product-card__row-label {
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--agent-product-row-label);
}

.product-card__row-value {
  color: var(--agent-product-card-text);
  word-break: break-word;
}

.product-card__row--block .product-card__row-value {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: var(--agent-product-card-text);
}

.product-card__footer {
  margin-top: auto;
  padding: 0.65rem 1rem 1rem 1.2rem;
}

.product-card__footer--split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.product-card__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0.45rem 0.65rem;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}

.product-card__btn--ghost {
  border-color: var(--agent-product-btn-ghost-border);
  background: var(--agent-product-btn-ghost-bg);
  color: var(--agent-product-btn-ghost-text);
}

.product-card__btn--ghost:hover {
  border-color: var(--agent-product-card-hover-border);
  background: var(--agent-product-btn-ghost-hover-bg);
  color: var(--agent-product-btn-ghost-hover-text);
}

.product-card__btn--accent {
  border-color: var(--agent-product-btn-accent-border);
  background: var(--agent-product-btn-accent-bg);
  color: var(--agent-product-btn-accent-text);
}

.product-card__btn--accent:hover {
  border-color: var(--agent-product-btn-accent-hover-border);
  box-shadow: var(--agent-product-btn-accent-hover-shadow);
  color: var(--agent-product-btn-accent-text);
}

.product-card__btn:focus-visible {
  outline: none;
  box-shadow: var(--agent-product-btn-focus-ring);
}
</style>
